<script lang="ts">
  import { onMount } from "svelte";
  import { errorTracker, postWithJwt, translate } from "lib";

  import {
    dialogData,
    selectedIds,
    showAlert,
    trainerClientGroups,
    trainerClientGroupsCount,
    trainerClientsStore
  } from "stores";

  import {
    ButtonComponent,
    CheckboxComponent,
    InputField,
    RadioField,
    Search
  } from "ui";

  import ClientListItem from "../../../components/ClientListItem.svelte";

  let data: any;
  let isLoading = false;
  let disabled = true;
  let step = 1;
  let value = "";
  let filteredClients = $trainerClientsStore.items.filter((client) => $selectedIds.includes(client.id));

  const onSearch = (): void => {};

  const form = {
    name: { value: "", error: "" },
    clientsCanPost: { value: false, error: "" },
    autoPostFinishedWorkout: { value: false, error: "" },
    notifySettings: { value: 0, error: "" },
  };

  const onInput = (field: keyof typeof form): void => {
    if (field === "name") {
      const { value } = form[field];

      if (value.length === 0) {
        form[field].error = translate("FIELD_REQUIRED");
      } else if (value.length < 2) {
        form[field].error = translate("FIELD_MINIMUM_2");
      } else if (value.length > 40) {
        form[field].error = translate("FIELD_MAXIMUM_40");
      } else {
        form[field].error = "";
      }
    }

    disabled = Object.values(form).some((field) => field.error !== "");
  };

  const onChange = (): void => {
    filteredClients = $trainerClientsStore.items.filter((client) => $selectedIds.includes(client.id));
  };

  const onNextStep = (): void => {
    step += 1;
  };
  const onPreviousStep = (): void => {
    step -= 1;
  };

  const editGroup = async (): Promise<void> => {
    try {
      const response = {};

      const group = $trainerClientGroups.find((group): boolean => group.id === data.group.id);

      const index = $trainerClientGroups.indexOf(group);

      $trainerClientGroups = $trainerClientGroups.with(index, response);

      $showAlert.color = "black";
      $showAlert.message = `
        ${translate("SUCCESSFULLY_EDITED")}
        ${translate("GROUP_A")}
      `;

      $dialogData.type = "";
      $dialogData.data = {};
    } catch (error) {
      isLoading = false;
      errorTracker.captureError(error, 'error', {
        context: "CreateEditGroup.editGroup",
        metadata: {
          groupId: data.group.id
        }
      });
      $showAlert.color = "red-400";
      $showAlert.message = translate("ERROR_EDITING_GROUP");
    }
  };

  const createGroup = async (): Promise<void> => {
    try {
      const response = await postWithJwt(``, {
        clientGroup: {
          name: form.name.value,
          clientsCanPost: form.clientsCanPost.value,
          autoPostFinishedWorkout: form.autoPostFinishedWorkout.value,
          autoPostCompletedCardio: true, // odakle ovo dolazi?
          notifySettings: form.notifySettings.value,
        },
        clientIds: $selectedIds,
      });

      $trainerClientGroups = [...$trainerClientGroups, response];
      $trainerClientGroupsCount += 1;

      $showAlert.color = "black";
      $showAlert.message = translate("SUCCESSFULLY_CREATED_GROUP");

      $dialogData.type = "";
      $dialogData.data = {};
    } catch (error) {
      isLoading = false;
      errorTracker.captureError(error, 'error', {
        context: "CreateEditGroup.createGroup",
        metadata: {
          clientIds: $selectedIds,
          groupName: form.name.value
        }
      });
      $showAlert.color = "red-400";
      $showAlert.message = translate("ERROR_CREATING_GROUP");
    }
  };

  const onSubmit = async (): Promise<void> => {
    isLoading = true;

    if (data.group) {
      await editGroup();
    } else {
      await createGroup();
    }
  };

  onMount((): void => {
    if (data.group) {
      // treba iz selectedIds da se izbaci vukasin? nzm zasto je on hidden svuda
      $selectedIds = data.group.chatSession.participants
        .map(({ userId }) => userId)
        .filter((id) => id !== 17833); // ovo je vukasinov ID

      form.name.value = data.group.name;
      form.clientsCanPost.value = data.group.clientsCanPost;
      form.autoPostFinishedWorkout.value = data.group.autoPostFinishedWorkout;
      form.notifySettings.value = data.group.notifySettings;
      // fali autoPostCompletedCardio, ali ne znam odakle to dolazi...
    }
  });

  export { data };
</script>

<div class="p-4 flex flex-col gap-4">
  <div class="text-center font-semibold">
    {data.group ? translate("EDIT_GROUP") : translate("CREATE_GROUP")}
  </div>

  <form class="flex flex-col gap-4" on:submit|preventDefault={onSubmit}>
    {#if step === 1}
      <div>1. {translate("SELECT_CLIENTS")}</div>

      <Search placeholder={translate("SEARCH_CLIENT")} bind:value on:input={onSearch} />

      <div class="pr-2 h-64 flex flex-col gap-4 overflow-y-scroll">
        {#each $trainerClientsStore.items as client}
          <ClientListItem {client} isSelectMode on:change={onChange} />
        {/each}
      </div>

      <div class="flex justify-end">
        <ButtonComponent disabled={$selectedIds.length < 2} on:click={onNextStep}>
          {translate("NEXT_STEP")}
        </ButtonComponent>
      </div>
    {:else if step === 2}
      <div>2. {translate("CLIENTS_PREVIEW")}</div>

      <div class="pr-2 h-64 flex flex-col gap-4 overflow-y-scroll">
        {#each filteredClients as client}
          <ClientListItem {client} isSelectMode on:change={onChange} />
        {/each}
      </div>

      <div class="flex justify-between">
        <ButtonComponent on:click={onPreviousStep}>
          {translate("PREVIOUS_STEP")}
        </ButtonComponent>
        <ButtonComponent
          disabled={$selectedIds.length < 2}
          on:click={onNextStep}
        >
          {translate("NEXT_STEP")}
        </ButtonComponent>
      </div>
    {:else}
      <div>3. {translate("GROUP_SETTINGS")}</div>

      <InputField
        label={translate("NAME")}
        error={form.name.error}
        bind:value={form.name.value}
        on:input={() => onInput("name")}
      />

      <div class="flex flex-col gap-2">
        <div>{translate("GROUP_SETTINGS")}:</div>
        <CheckboxComponent
          label={translate("CLIENTS_CAN_POST")}
          bind:value={form.clientsCanPost.value}
        />
        <CheckboxComponent
          label={translate("AUTO_POST_FINISHED_WORKOUT")}
          bind:value={form.autoPostFinishedWorkout.value}
        />
      </div>

      <div class="flex flex-col gap-2">
        <div>{translate("NOTIFICATIONS_SETTINGS")}:</div>
        <RadioField
          value="0"
          fontSize="xs"
          bind:group={form.notifySettings.value}
        >
          {translate("ALWAYS")}
        </RadioField>
        <RadioField
          value="1"
          fontSize="xs"
          bind:group={form.notifySettings.value}
        >
          {translate("ON_MENTION_ONLY")}
        </RadioField>
        <RadioField
          value="2"
          fontSize="xs"
          bind:group={form.notifySettings.value}
        >
          {translate("NEVER")}
        </RadioField>
      </div>

      <div class="flex justify-between">
        <ButtonComponent on:click={onPreviousStep}>
          {translate("PREVIOUS_STEP")}
        </ButtonComponent>
        <ButtonComponent type="submit" {disabled} {isLoading}>
          {data.group ? translate("EDIT") : translate("CREATE")}
        </ButtonComponent>
      </div>
    {/if}
  </form>
</div>
